import type { PurchaseDetail } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { Alert, Button, Descriptions, Space, Spin, Table, Tag } from 'antd';
import { useIsMobile } from '../../components/useMobile';
import { ellipsisCell } from '@hq/ui-kit';

/**
 * 采购订单详情正文（从 PurchaseOps 抽屉下沉）：只负责展示 + 触发接单/收货。
 */
export default function PurchaseDetailBody({
  detail,
  loading,
  error,
  acking,
  onAck,
  onReceive,
  statusMeta
}: {
  detail?: PurchaseDetail;
  loading: boolean;
  error: unknown;
  acking: boolean;
  onAck: (id: string) => void;
  onReceive: () => void;
  statusMeta: Record<string, { text: string; color: string }>;
}) {
  const isMobile = useIsMobile();
  if (loading) return <Spin />;
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="详情加载失败"
        description={error instanceof ApiError ? error.friendlyMessage : String(error)}
      />
    );
  }
  if (!detail) return null;

  return (
    <>
      <Descriptions size="small" column={isMobile ? 1 : 2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="状态">{statusMeta[detail.order.orderStatus]?.text ?? detail.order.orderStatus}</Descriptions.Item>
        <Descriptions.Item label="供应商">{detail.order.supplierName || '-'}</Descriptions.Item>
        <Descriptions.Item label="总数量">{formatQty(detail.order.totalQty)}</Descriptions.Item>
        <Descriptions.Item label="总金额">{formatAmount(detail.order.totalAmount)}</Descriptions.Item>
        <Descriptions.Item label="已收数量">{formatQty(detail.order.receivedQty)}</Descriptions.Item>
        <Descriptions.Item label="已收金额">{formatAmount(detail.order.receivedAmount)}</Descriptions.Item>
        <Descriptions.Item label="接单时间">{formatDisplayTime(detail.order.supplierAckAt)}</Descriptions.Item>
        <Descriptions.Item label="预计到货">{formatDisplayTime(detail.order.expectArriveAt)}</Descriptions.Item>
      </Descriptions>

      <Space style={{ marginBottom: 16 }}>
        {['SENT', 'SUBMITTED', 'APPROVED'].includes(detail.order.orderStatus) ? (
          <Button loading={acking} onClick={() => onAck(detail.order.id)}>
            代供应商接单
          </Button>
        ) : null}
        {!['RECEIVED', 'CLOSED', 'CANCELED', 'TERMINATED'].includes(detail.order.orderStatus) ? (
          <Button type="primary" onClick={onReceive}>
            到货收货
          </Button>
        ) : null}
      </Space>

      <Table
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ x: 900 }}
        dataSource={detail.items}
        columns={[
          { title: '行', dataIndex: 'lineNo', width: 60 },
          { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
          { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
          { title: '订购量', dataIndex: 'qty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
          { title: '已收', dataIndex: 'receivedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
          { title: '单价', dataIndex: 'unitPrice', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
          { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
          {
            title: '行状态',
            dataIndex: 'lineStatus',
            width: 110,
            render: (value: string | null) => {
              if (!value) return '-';
              const meta = statusMeta[value] ?? { text: value, color: 'default' };
              return <Tag color={meta.color}>{meta.text}</Tag>;
            }
          }
        ]}
      />
    </>
  );
}
